import { useCallback, useEffect, useRef, useState } from 'react';

import { getGlobalConfig } from '../utils/getGlobalConfig';

interface GooglePlacesPredictions {
  predictions: google.maps.places.AutocompletePrediction[];
  loading: boolean;
  scriptLoaded: boolean;
  fetchPredictions: (inputValue: string) => Promise<void>;
}

export const useGooglePlacesPredictions = (): GooglePlacesPredictions => {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const { googleApiKey } = getGlobalConfig();

  const fetchPredictions = async (inputValue: string) => {
    if (!inputValue.trim() || !autocompleteService.current) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      const request: google.maps.places.AutocompletionRequest = {
        input: inputValue,
        types: ['geocode', 'establishment']
      };

      autocompleteService.current.getPlacePredictions(
        request,
        (
          results: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          setLoading(false);
          const isOk = status === google.maps.places.PlacesServiceStatus.OK && results;
          setPredictions(isOk ? results : []);
        }
      );
    } catch {
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleMapsScript = useCallback(() => {
    if (!googleApiKey) {
      return;
    }

    if (window.google?.maps) {
      setScriptLoaded(true);
      autocompleteService.current = new google.maps.places.AutocompleteService();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setScriptLoaded(true);
      autocompleteService.current = new google.maps.places.AutocompleteService();
    };
    script.onerror = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, [googleApiKey]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => loadGoogleMapsScript(), 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadGoogleMapsScript]);

  return {
    predictions,
    loading,
    scriptLoaded,
    fetchPredictions
  };
};
